import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchForStaticTable, selectedChange, tableChange } from '../../actions/table.actions';
import Table from '../../components/tables/Table';
import { DEFAULT_PAGE } from '../../constants/table.constants';
import { usePreferences } from '../../hooks/usePreferences.hook';
import { currentUserSelector } from '../../selectors/auth.selectors';
import { fetchingSelector, shouldFetchSelector } from '../../selectors/fetch.selectors';
import { tableDataSelector, tableSelectedSelector } from '../../selectors/resources.selectors';

const StaticTable = ({ resource, legend, actions, forceLoading, data: staticData, responsive, onRowClick }) => {
  const dispatch = useDispatch();
  const shouldFetch = useSelector((state) => shouldFetchSelector(state, { resource }));
  const isLoading = useSelector((state) => fetchingSelector(state, { resource }));
  const { data, config } = useSelector((state) => tableDataSelector(state, { resource }));
  const user = useSelector(currentUserSelector);
  const { fields, sort, size, isFetched, onSortChange, onPageSizeChange, onOrderField, onEnableField } = usePreferences(
    user,
    resource
  );
  const selected = useSelector((state) => tableSelectedSelector(state, { resource }));
  const [page, setPage] = useState(DEFAULT_PAGE);

  const handleChange = (change) => {
    if (page !== change?.page) {
      setPage(change?.page);
    }

    if (isLoading) {
      return;
    }

    onPageSizeChange(change.size);
    onSortChange(change.sort);
    dispatch(tableChange(resource, change));
  };

  const handleSelectAll = () => {
    dispatch(selectedChange(resource, selected.length > 0 ? [] : data?.map((i) => i.id)));
  };

  const handleSelectSingle = (selectedId) => {
    if (selected.includes(selectedId)) {
      dispatch(
        selectedChange(
          resource,
          selected.filter((s) => s !== selectedId)
        )
      );
    } else {
      dispatch(selectedChange(resource, [...selected, selectedId]));
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted && isFetched && shouldFetch) {
      dispatch(
        fetchForStaticTable(
          resource,
          {
            _page: { size, totalElements: staticData?.length ?? 0, number: page },
            _embedded: { static: staticData }
          },
          { sort, size }
        )
      );
    }

    return () => {
      isMounted = false;
    };
  }, [shouldFetch, isFetched, sort, dispatch, staticData, page, size, resource]);

  return (
    <Table
      resourceKey={resource.key}
      emptyMessage={resource?.table?.emptyMessage}
      title={resource.table?.title}
      required={resource.table?.required}
      legend={legend}
      actions={actions}
      isLoading={forceLoading || isLoading || !isFetched}
      filtersEnabled={false}
      onRowClick={onRowClick}
      onChange={handleChange}
      onFieldEnableChange={onEnableField}
      onFieldOrderChange={onOrderField}
      onSelect={handleSelectSingle}
      onSelectAll={handleSelectAll}
      selected={selected}
      data={data}
      fields={fields}
      responsive={responsive}
      withPagination={!resource?.table?.disablePagination}
      enableSelect={resource?.table?.enableSelect}
      config={{ ...config, sort: sort || config.sort, size: size || config.size }}
    />
  );
};

export default StaticTable;
